/* ============================================================
   Operational dialogs (port of modals.js 393–755):
   delete, container logs/shell, claim a device, catalog install,
   playbooks/schedules/repo, stack versions, drift, small workload
   dialogs, restore/replace, rollouts, alert rules, sites, images,
   private apps.
   ============================================================ */
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import {
  DB, api, job, act, load, toast, go, can, fire, wsUrl,
  node, site, wl, stackById, ctrById, nodeOf, siteNameOf, type Any,
} from "../lib/store";
import { ago, when, gb } from "../lib/fmt";
import { Badge, Copyable, copy } from "../ui/common";
import { Modal, Frame, Btn, F, FormCtx, useForm, type Form } from "../ui/modal";
import { TermBox } from "../ui/term";
import { canRelax, deployStack, SAMPLE_COMPOSE } from "./deploy";

/* ---------- helpers ---------- */
type Opt = string | number | [Any, ReactNode];
const optVal = (o: Opt): Any => (Array.isArray(o) ? o[0] : o);
/* the value an old <select> ended up with: `val` if it is one of the options, else the first option */
function pick(opts: Opt[], val?: Any): Any {
  if (val != null && opts.some((o) => String(optVal(o)) === String(val))) return String(val);
  return opts.length ? String(optVal(opts[0])) : "";
}
const uniq = <T,>(a: T[]): T[] => [...new Set(a)];
const allTags = (): string[] => uniq<string>(DB.workloads.flatMap((w: Any) => w.tags || []));

/* the old Modal.simple(title, body-with-F-fields, action, fn): a form-backed dialog */
function FormModal({ title, init, body, action, fn, wide, footer }:
  { title: ReactNode; init: Any; body: (f: Form) => ReactNode; action?: string; fn?: (d: Any, f: Form) => Any; wide?: boolean;
    footer?: (f: Form) => ReactNode }) {
  const f = useForm(init);
  return (
    <FormCtx.Provider value={f}>
      <Frame title={title} wide={wide} footer={footer ? footer(f) : <>
        <div className="spacer" />
        <button className="btn" onClick={Modal.close}>Cancel</button>
        {action && fn ? <Btn primary onClick={() => fn(f.d, f)}>{action}</Btn> : null}
      </>}>{body(f)}</Frame>
    </FormCtx.Provider>
  );
}
function simpleForm(title: ReactNode, init: Any, body: (f: Form) => ReactNode, action?: string, fn?: (d: Any, f: Form) => Any, wide?: boolean) {
  Modal.open(<FormModal title={title} init={init} body={body} action={action} fn={fn} wide={wide} />);
}

/* ============================ delete ============================ */
export function deleteItem(kind: string, id: Any) {
  let name = "", subject = "", children: string[] = [], warn = "", path = "";
  let extra: ReactNode = null;
  const init: Any = { delname: "" };
  if (kind === "workload") {
    const w = wl(id); if (!w) return;
    name = w.name; subject = w.external ? `imported machine ${w.name}` : `${w.type.toUpperCase()} ${w.name} (vmid ${w.vmid})`;
    const st = DB.stacks.filter((s: Any) => s.workload === w.id), cs = DB.containers.filter((c: Any) => c.workload === w.id);
    children = w.external ? ["its record in this console (the machine itself is not touched)"] :
      [`${w.dsize} GB disk`, ...(st.length ? [`${st.length} stack(s): ${st.map((s: Any) => s.name).join(", ")}`] : []),
        ...(cs.length ? [`${cs.length} container(s): ${cs.map((c: Any) => c.name).join(", ")}`] : []),
        ...(w.snapshots ? [`${w.snapshots} snapshot${w.snapshots > 1 ? "s" : ""}`] : []), ...(w.mgmt_ip ? ["its overlay address " + w.mgmt_ip] : []),
        "backup schedules and alert history for it"];
    if ((w.tags || []).includes("prod")) warn = "This workload is tagged prod and may be serving live traffic.";
    if (nodeOf(w).settings?.delete_protection) warn = "Delete protection is on for " + nodeOf(w).name + " — the job will refuse until it is switched off.";
    const est = Math.max(1, Math.round(w.dsize * (w.disk || 30) / 100));
    if (!w.external) {
      init.backup = true; init.keepdisk = false; init.invonly = false;
      extra = <>
        <F.Check k="backup" title="Take a final backup first" sub={`Roughly ${est} GB to the device's backup storage.`} />
        {w.type === "vm" ? <F.Check k="keepdisk" title="Keep the disks when it is purged" sub="The disks move to a stopped “ec-disk-holder” VM instead of being destroyed." /> : null}
        <F.Check k="invonly" title="Remove from inventory only" sub="Leaves everything running on the device and stops managing it here." />
      </>;
    }
    path = `/api/workloads/${id}/delete`;
  } else if (kind === "node") {
    const n = node(id); if (!n) return;
    name = n.name; subject = `edge device ${n.name}`;
    children = [`${DB.workloads.filter((w: Any) => w.node === n.id).length} guests stay on the device but stop being managed`,
      "the agent, its Proxmox API token and the console SSH key on the device", "metrics and job history links"];
    warn = "The device must be enrolled and claimed again before it can be managed.";
    path = `/api/nodes/${id}`;
  } else if (kind === "stack") {
    const s = stackById(id); if (!s) return;
    name = s.name; subject = `stack ${s.name}`;
    children = [`${s.services} container(s)`, "its compose file on the machine", "named volumes (only if you tick the box)"];
    init.vol = false;
    extra = <F.Check k="vol" title="Also delete named volumes" sub="Database contents are gone for good once this runs." />;
    path = `/api/stacks/${id}/delete`;
  } else if (kind === "container") {
    const c = ctrById(id); if (!c) return;
    name = c.name; subject = `container ${c.name}`; children = ["the container and its writable layer (volumes are kept)"];
    if (c.stack) warn = "This container belongs to a compose stack — it comes back on the next redeploy.";
    path = `/api/containers/${id}/delete`;
  } else {
    const i = DB.images.find((x: Any) => String(x.id) === String(id)); if (!i) return;
    name = i.name; subject = `image ${i.name}`; children = [`copies on ${i.cached_n} device(s)`]; path = `/api/images/${id}/delete`;
  }
  const doDelete = async (d: Any) => {
    const confirm = String(d.delname || "").trim();
    if (kind === "workload") return job("POST", path, { backup: !!d.backup, keepdisk: !!d.keepdisk, invonly: !!d.invonly, confirm });
    if (kind === "node") return job("DELETE", path).then(() => {});
    if (kind === "stack") return job("POST", path, { volumes: !!d.vol, confirm });
    if (kind === "container") return job("POST", path, { confirm });
    const r = await api("POST", path, { confirm }).catch((e: Any) => { toast("Failed", e.message, "err"); throw e; });
    Modal.close(); if (r.job) { go("job", r.job); } else { toast("Image deleted"); } load().catch(() => {});
  };
  Modal.open(
    <FormModal title={`Delete ${name}?`} init={init}
      body={(f) => <>
        {warn ? <div className="banner"><span>△</span><div><b>{warn}</b></div></div> : null}
        <p style={{ marginTop: 0 }}>Deleting <b>{subject}</b> also removes:</p>
        <ul style={{ color: "var(--ink-2)", margin: "0 0 16px", paddingLeft: 20 }}>{children.map((c, i) => <li key={i}>{c}</li>)}</ul>
        {extra}
        <label className="f" style={{ marginTop: 14 }}><span>Type <Copyable v={name} cls="strong" /> to confirm</span>
          <input type="text" id="delname" placeholder={name} value={f.d.delname ?? ""} onChange={(e) => f.set("delname", e.target.value)} /></label>
      </>}
      footer={(f) => <>
        <div className="spacer" />
        <button className="btn" onClick={Modal.close}>Cancel</button>
        <Btn danger id="delbtn" disabled={String(f.d.delname || "").trim() !== name} onClick={() => doDelete(f.d)}>
          Delete{kind === "workload" ? "" : " permanently"}</Btn>
      </>} />,
  );
}

/* ============================ container logs ============================ */
function LogsModal({ c }: { c: Any }) {
  const w = wl(c.workload);
  const bad = c.restarts > 3 || c.status === "restarting" || c.health === "unhealthy";
  const [text, setText] = useState<string | null>(null);
  const [following, setFollowing] = useState(false);
  const ws = useRef<WebSocket | null>(null);
  const pre = useRef<HTMLPreElement>(null);
  const stop = () => { if (ws.current) { ws.current.close(); ws.current = null; } setFollowing(false); };
  useEffect(() => {
    let alive = true;
    (async () => {
      try { const t = await api("GET", `/api/containers/${c.id}/logs?tail=300`); if (alive) setText(String(t || "") || "(no output)"); }
      catch (e: Any) { if (alive) setText(e.message); }
    })();
    return () => { alive = false; if (ws.current) { ws.current.close(); ws.current = null; } };
  }, [c.id]);
  useLayoutEffect(() => { const p = pre.current; if (p) p.scrollTop = p.scrollHeight; }, [text]);
  const follow = () => {
    if (ws.current) { stop(); return; }
    setText("");
    const s = new WebSocket(wsUrl(`/ws/logs/${c.id}`));
    ws.current = s;
    s.onmessage = (e) => setText((t) => (t || "") + e.data + "\n");
    setFollowing(true);
    toast("Following", "New lines appear as they arrive");
  };
  const sk = stackById(c.stack);
  return (
    <Frame title={`Logs · ${c.name}`} footer={<>
      <div className="spacer" />
      <button className="btn" onClick={() => { stop(); Modal.close(); }}>Close</button>
      {can("Operator") ? (bad && c.stack
        ? <button className="btn primary" onClick={() => { stop(); fire(deployStack(c.workload, sk?.kind || "compose", c.stack)); }}>Edit the stack</button>
        : <button className="btn" onClick={() => { stop(); fire(job("POST", `/api/containers/${c.id}/restart`, {})); }}>Restart</button>) : null}
    </>}>
      <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap", alignItems: "center" }}>
        <Badge s={c.status} /><span className="badge">{c.image}</span><span className="badge">{c.restarts} restarts</span><span className="badge">health: {c.health}</span>
        <div style={{ flex: 1 }} /><button className="btn sm" id="followbtn" onClick={follow}>{following ? "Stop following" : "Follow"}</button>
        <a className="btn sm ghost" href={`/api/containers/${c.id}/logs?tail=20000&download=1`}>Download</a>
      </div>
      <pre className="log" id="clog" ref={pre} style={{ maxHeight: 460 }}>{text === null ? <span className="spin" /> : text}</pre>
      {bad ? <div className="banner" style={{ marginTop: 12 }}><span>△</span><div><b>This container keeps failing</b>The last lines above usually say why: a missing variable, a wrong hostname between services, or an image built for another CPU (this device is {(w ? nodeOf(w).arch : null) || "?"}).</div></div> : null}
    </Frame>
  );
}
export function logs(id: Any) {
  const c = ctrById(id); if (!c) return;
  Modal.open(<LogsModal c={c} />, true);
}

/* ============================ shell in a modal (containers) ============================ */
export function shell(target: string, label: string) {
  Modal.open(
    <Frame title={`Shell · ${label}`} footer={<><div className="spacer" /><button className="btn" onClick={Modal.close}>Close</button></>}>
      <TermBox target={target} /><div className="hint">Session recorded. Close this window to end the session.</div>
    </Frame>, true,
  );
}

/* ============================ claim / add device ============================ */
async function scanQR(f: Form) {
  if (!("BarcodeDetector" in window)) { toast("Camera scanning not supported here", "Type the code instead", "warn"); return; }
  try {
    const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
    const v = document.createElement("video"); v.srcObject = stream; await v.play();
    const det = new (window as Any).BarcodeDetector({ formats: ["qr_code"] });
    const t0 = Date.now();
    while (Date.now() - t0 < 20000) {
      const r = await det.detect(v);
      if (r.length) { f.set("code", r[0].rawValue); toast("Code scanned", r[0].rawValue); break; }
      await new Promise((res) => setTimeout(res, 300));
    }
    stream.getTracks().forEach((t) => t.stop());
  } catch (e: Any) { toast("Camera unavailable", e.message, "warn"); }
}
function claimDevice(d: Any) {
  const s = d.site;
  return job("POST", "/api/claim", { code: d.code, name: d.cname || null, site_id: s === "new" ? null : +s, new_site: s === "new" ? d.newsite : null,
    blueprint_id: d.blueprint ? +d.blueprint : null });
}
function manualDevice(d: Any) {
  const s = d.site;
  return job("POST", "/api/nodes/manual", { name: d.mname, host: d.mhost, token_id: d.mtid, token_secret: d.mtsec,
    ssh_user: d.muser, port: +d.mport, site_id: s === "new" ? null : +s, new_site: s === "new" ? d.newsite : null, install_agent: !!d.magent });
}
function ClaimModal({ tab }: { tab: string }) {
  const siteOpts: Opt[] = DB.sites.map((s: Any) => [s.id, s.name] as [Any, ReactNode]).concat([["new", "New site…"]]);
  const f = useForm({
    site: pick(siteOpts, (DB.sites[0] || {}).id), newsite: "", code: "", cname: "", blueprint: "",
    mname: "", mhost: "", mtid: "root@pam!ec-console", mtsec: "", muser: "root", mport: 8006, magent: false,
  });
  const T = (k: string, l: string) => <button className={tab === k ? "on" : ""} onClick={() => claim(k)}>{l}</button>;
  const install = `curl -fsSL ${DB.meta.public_url}/api/agent/install.sh | bash -s -- ${DB.meta.enroll_key}`;
  const siteSel = <>
    <F.Sel k="site" label="Assign to site" opts={siteOpts} />
    <div id="newsite" style={{ display: f.d.site === "new" ? "block" : "none" }}><F.Text k="newsite" label="New site name" /></div>
  </>;
  let body: ReactNode = null, action: string | null = null, fn: ((d: Any) => Any) | null = null;
  if (tab === "code") {
    body = <>
      <p style={{ marginTop: 0, color: "var(--ink-2)" }}>Type the claim code the device printed when its agent was installed (also in <code>/etc/edge-agent/claim-code</code> on the device). The device does the rest on its own.</p>
      <div style={{ display: "flex", gap: 16, alignItems: "flex-start", flexWrap: "wrap" }}>
        <div style={{ width: 120, textAlign: "center" }}>
          <div style={{ width: 120, height: 120, border: "1px solid var(--line)", borderRadius: 8, display: "grid", placeItems: "center", fontSize: 40, background: "var(--surface-2)", cursor: "pointer" }}
            onClick={() => fire(scanQR(f))} title="Scan with camera">▦</div>
          <div className="hint">scan QR</div></div>
        <div style={{ flex: 1, minWidth: 240 }}>
          <F.Text k="code" label="Claim code" ph="EC-XXXX-XXXX" />
          <F.Text k="cname" label="Device name (optional)" hint="Defaults to the hostname." />
          {siteSel}
          <F.Sel k="blueprint" label="Apply a blueprint" opts={([["", "None — leave it empty"]] as Opt[]).concat(DB.blueprints.map((b: Any) => [b.id, b.name] as [Any, ReactNode]))} />
        </div></div>
      {DB.unclaimed.length ? <>
        <div className="section-title">Waiting to be claimed</div>
        <div className="card"><div className="bd" style={{ padding: 0 }}>{DB.unclaimed.map((u: Any, i: number) => (
          <div key={u.id ?? i} style={{ padding: "10px 14px", borderBottom: "1px solid var(--line-2)" }}>
            <b style={{ fontWeight: 500 }}>{u.name}</b> <span className="muted mono">{u.ip} · {ago(u.last_seen)}</span>
            <div className="muted" style={{ fontSize: 12 }}>{u.model}</div></div>))}</div></div>
      </> : null}
      <div className="banner acc" style={{ marginTop: 14 }}><span>◈</span><div><b>What happens next</b>The device joins this network, its Proxmox inventory is read, and the blueprint (if any) creates its guests. Watch it on the job page.</div></div>
    </>;
    action = "Claim device"; fn = claimDevice;
  } else if (tab === "agent") {
    body = <>
      <p style={{ marginTop: 0, color: "var(--ink-2)" }}>Run this once as root on the Proxmox host. It creates an API token, authorises the console SSH key, installs the agent and prints a claim code.</p>
      <pre className="log copy" style={{ whiteSpace: "pre-wrap", wordBreak: "break-all" }} onClick={() => copy(install)} title="Click to copy">{install}</pre>
      <div className="hint">The device must reach <code>{DB.meta.public_url}</code> (set <code>PUBLIC_URL</code> in .env). Over Tailscale, allow the device to reach this console's port in your tailnet ACL.</div>
      <div className="btnrow" style={{ marginTop: 12 }}><button className="btn" onClick={() => copy(install)}>Copy command</button><button className="btn primary" onClick={() => claim("code")}>I have a claim code</button></div>
    </>;
  } else {
    body = <>
      <p style={{ marginTop: 0, color: "var(--ink-2)" }}>For a host that cannot reach this console: the console connects to its API and SSH instead. On the host, create a token with <code>pveum user token add root@pam ec-console --privsep 0</code> and authorise this key for root:</p>
      <pre className="log copy" style={{ whiteSpace: "pre-wrap", wordBreak: "break-all" }} onClick={() => copy(DB.meta.pubkey)} title="Click to copy">{DB.meta.pubkey}</pre>
      <div className="btnrow" style={{ margin: "-4px 0 14px" }}><button className="btn sm" onClick={() => copy(DB.meta.pubkey)}>Copy key</button><button className="btn sm ghost" onClick={() => copy("pveum user token add root@pam ec-console --privsep 0")}>Copy token command</button></div>
      <div className="grid g2"><F.Text k="mname" label="Device name" /><F.Text k="mhost" label="Address (IP or Tailscale name)" ph="100.x.y.z" /></div>
      <div className="grid g2"><F.Text k="mtid" label="Token ID" /><F.Pass k="mtsec" label="Token secret" /></div>
      <div className="grid g2"><F.Text k="muser" label="SSH user" /><F.Num k="mport" label="API port" /></div>
      {siteSel}
      <F.Check k="magent" title="Install the edge agent too" sub="Only useful if the device can reach this console (heartbeats, temperature, offline detection)." />
    </>;
    action = "Connect device"; fn = manualDevice;
  }
  return (
    <FormCtx.Provider value={f}>
      <Frame title="Add an edge device" footer={<>
        <div className="spacer" />
        <button className="btn" onClick={Modal.close}>Cancel</button>
        {action && fn ? <Btn primary id="m-ok" onClick={() => fn!(f.d)}>{action}</Btn> : null}
      </>}>
        <div className="pill-toggle" style={{ marginBottom: 16 }}>{T("agent", "1 · Install agent")}{T("code", "2 · Claim with code")}{T("manual", "Connect manually")}</div>
        {body}
      </Frame>
    </FormCtx.Provider>
  );
}
export function claim(tab?: string) {
  const t = tab || (DB.unclaimed.length ? "code" : "agent");
  Modal.open(<ClaimModal tab={t} />, true);
}

/* ============================ catalog install ============================ */
export async function installApp(id: Any) {
  let a: Any;
  try { a = await api("GET", "/api/catalog/" + id); } catch (e: Any) { toast("Failed", e.message, "err"); return; }
  const targets = DB.workloads.filter((w: Any) => w.docker);
  if (!targets.length) { toast("No Docker host yet", "Create a VM or LXC with Docker first", "warn"); return; }
  const vars = uniq([...String(a.compose).matchAll(/\$\{(\w+)(?::-([^}]*))?\}/g)].map((m) => m[1] + "=" + (m[2] || "")));
  const tOpts: Opt[] = targets.map((w: Any) => [w.id, `${w.name} — ${siteNameOf(w)} (${w.ram}% memory used, ${nodeOf(w).arch || "?"})`] as [Any, ReactNode]);
  const relax = canRelax();
  const init: Any = { target: pick(tOpts), tunnel: false, backup: true, yaml: a.compose, relaxed: false };
  if (vars.length) init.env = vars.join("\n");
  simpleForm(`Install ${a.name}`, init, (f) => <>
    <p style={{ marginTop: 0, color: "var(--ink-2)" }}>{a.desc} Needs about {a.ram} of memory.</p>
    <F.Sel k="target" label="Install on" opts={tOpts} />
    {a.port ? <F.Check k="tunnel" title="Publish it on a secure URL" sub={`Tailscale Funnel on port ${a.port}. Needs an auth key in Settings.`} /> : null}
    <F.Check k="backup" title="Add the machine to the nightly backup" sub="Only if it has no schedule yet." />
    {vars.length ? <F.Area k="env" label="Variables (.env)" rows={Math.min(6, vars.length + 1)} hint="Fill in passwords here; they are stored encrypted." /> : null}
    <div className="editor" style={{ marginTop: 12, minHeight: 150 }}>
      <div className="eh"><b>Generated compose file</b><div className="spacer" /><span style={{ color: "var(--ink-3)" }}>pinned versions, limits set — edit if you need to</span></div>
      <textarea spellCheck={false} style={{ minHeight: 200 }} value={f.d.yaml ?? ""} onChange={(e) => f.set("yaml", e.target.value)} /></div>
    {relax ? <F.Check k="relaxed" title="Relaxed validation for this install (Admin override)" sub="Missing memory limits, :latest tags, host networking and privileged containers become warnings. Recorded in the audit log." /> : null}
  </>, "Install", (d) => job("POST", `/api/catalog/${a.id}/install`, {
    target: +d.target, yaml: d.yaml, env: d.env || "", tunnel: !!d.tunnel, backup: !!d.backup, relaxed: !!d.relaxed,
  }), true);
}

/* ============================ playbooks ============================ */
export function scopeOptions(extra?: string): string[] {
  const tags = allTags();
  return [...(extra ? [extra] : []), "Everything in this network", "All edge nodes", "All workloads", ...DB.sites.map((s: Any) => "Site: " + s.name),
    ...tags.map((t) => "Tag: " + t), ...DB.nodes.map((n: Any) => "Device: " + n.name), ...DB.workloads.map((w: Any) => "Workload: " + w.name)]
    .filter((v, i, a) => a.indexOf(v) === i);
}
async function doRunPlaybook(d: Any) {
  try {
    const r = await api("POST", "/api/playbooks/run", { playbook: d.pb, scope: d.scope, when: d.when, check: !!d.check, serial: !!d.serial, cron: d.cron });
    Modal.close();
    if (r.job) { toast("Job started", r.job); await load(); go("job", r.job); }
    else { toast("Job scheduled", r.next ? "Next run " + when(r.next) : ""); await load(); go("jobs"); }
  } catch (e: Any) { toast("Could not run", e.message, "err"); }
}
export function runPlaybook(target?: string, pb?: string, dry?: boolean) {
  const pbOpts: Opt[] = DB.playbooks.map((p: Any) => [p.name, `${p.name} — ${p.desc}`] as [Any, ReactNode]);
  const scOpts = scopeOptions(target);
  const whenOpts = ["Now", "Next maintenance window", "Schedule…"];
  simpleForm(dry ? "Dry run a playbook" : "Run a playbook",
    { pb: pick(pbOpts, pb), scope: pick(scOpts, target), when: "Now", cron: "Every Sunday 02:00", check: !!dry, serial: true },
    (f) => <>
      <F.Sel k="pb" label="Playbook" opts={pbOpts} />
      <F.Sel k="scope" label="Run against" opts={scOpts} />
      <F.Sel k="when" label="When" opts={whenOpts} />
      <div id="cronbox" style={{ display: String(f.d.when || "").startsWith("Schedule") ? "block" : "none" }}>
        <F.Text k="cron" label="Schedule" hint="Plain words (“Daily 01:00”, “Every Sunday 02:00”, “First of the month 03:00”) or a cron line." /></div>
      <F.Check k="check" title="Check mode (nothing is changed)" sub="Shows a diff of what would happen. Recommended before a real run." />
      <F.Check k="serial" title="One host at a time" sub="Slower, but a failure cannot take down a whole site." />
    </>, dry ? "Start dry run" : "Run playbook", doRunPlaybook);
}
async function scheduleJob(d: Any) {
  try {
    const r = await api("POST", "/api/schedules", { playbook: d.pb, scope: d.scope, cron: d.cron, window: !!d.window, notify: !!d.notify });
    Modal.close(); toast("Job scheduled", r.next ? "Next run " + when(r.next) : ""); await load(); go("jobs");
  } catch (e: Any) { toast("Not scheduled", e.message, "err"); }
}
export function schedule() {
  const pbOpts: Opt[] = DB.playbooks.map((p: Any) => p.name);
  const scOpts = scopeOptions();
  simpleForm("Schedule a job", { pb: pick(pbOpts), scope: pick(scOpts), cron: "Every Sunday 02:00", window: true, notify: true }, () => <>
    <F.Sel k="pb" label="Playbook" opts={pbOpts} /><F.Sel k="scope" label="Against" opts={scOpts} />
    <F.Text k="cron" label="When" hint="“Daily 01:00”, “Every Sunday 02:00”, “First of the month 03:00”, “Every 6 hours”, or cron." />
    <F.Check k="window" title="Only inside the maintenance window" /><F.Check k="notify" title="Tell me if it fails" sub="Uses the notification channels in Settings." />
  </>, "Schedule", scheduleJob);
}
export function repo() {
  const r = DB.settings.playbooks || {};
  simpleForm("Playbook repository", { url: r.repo_url || "", token: "", clear_token: false, branch: r.branch || "main" }, () => <>
    <F.Text k="url" label="Git URL" hint="https URL of the repository, e.g. https://github.com/org/repo.git (or an ssh:// / git@ URL)." ph="https://github.com/org/playbooks.git" />
    <F.Pass k="token" label="Access token (private repositories)" ph={r.has_token ? "•••••••• saved — type to replace" : "leave empty for a public repository"}
      hint={<>A personal access / deploy token with read access (GitHub, GitLab, Gitea). For Bitbucket use <code>username:app-password</code>. Stored encrypted and sent to git as an auth header, never in the URL or logs.</>} />
    {r.has_token ? <F.Check k="clear_token" title="Remove the saved token" sub="Use this when the repository became public." /> : null}
    <F.Text k="branch" label="Branch" />
    <p className="hint">Playbooks in the repo root or a <code>playbooks/</code> folder appear on this page. Add <code># desc:</code> and <code># targets: guests|nodes|any</code> comment lines at the top.</p>
  </>, "Save and sync", (d) => job("POST", "/api/playbooks/repo", { repo_url: d.url, token: d.token || "", clear_token: !!d.clear_token, branch: d.branch }));
}

/* ============================ stack versions ============================ */
export async function versions(id: Any) {
  const s = stackById(id);
  let vs: Any[] = [];
  try { vs = await api("GET", `/api/stacks/${id}/versions`); } catch (e: Any) { toast("Failed", e.message, "err"); return; }
  Modal.simple(`Version history · ${s ? s.name : id}`, vs.length ? (
    <table><thead><tr><th>Version</th><th>Deployed</th><th>By</th><th>Note</th><th></th></tr></thead><tbody>
      {vs.map((v: Any) => (
        <tr key={v.id}><td className="mono">v{v.version}</td><td>{when(v.created)}</td><td>{v.by}</td><td>{v.note}</td>
          <td style={{ textAlign: "right" }}><div className="btnrow" style={{ justifyContent: "flex-end" }}>
            <button className="btn sm ghost" onClick={() => fire(viewVersion(v.id, v.version, id))}>View</button>
            {v.current ? <span className="badge ok">current</span> : can("Operator")
              ? <Btn sm onClick={() => job("POST", `/api/stacks/${id}/rollback`, { version_id: v.id })}>Roll back</Btn> : null}
          </div></td></tr>))}
    </tbody></table>
  ) : <p>This stack was found on the host and has no versions stored here yet. Edit and redeploy it once to start the history.</p>, undefined, undefined, true);
}
export async function viewVersion(vid: Any, ver: Any, sid: Any) {
  const t = await api("GET", `/api/stack-versions/${vid}`);
  Modal.open(
    <Frame title={`v${ver}`} wide footer={<>
      <button className="btn" onClick={() => fire(versions(sid))}>← Back</button>
      <div className="spacer" />
      <button className="btn" onClick={Modal.close}>Cancel</button>
    </>}><pre className="log" style={{ maxHeight: 520 }}>{typeof t === "string" ? t : JSON.stringify(t, null, 2)}</pre></Frame>,
  );
}

/* ============================ drift ============================ */
export function drift(id: Any) {
  const w = wl(id); if (!w) return;
  const dr: Any[] = w.drift || [];
  const fw = dr.find((d: Any) => d.key === "firewall");
  Modal.open(
    <Frame title={`Drift on ${w.name}`} footer={<>
      <div className="spacer" />
      <button className="btn" onClick={Modal.close}>Close</button>
      {can("Operator") ? <>
        <Btn onClick={() => job("POST", `/api/workloads/${w.id}/drift`, { op: "adopt" })}>Adopt what is running</Btn>
        <Btn primary onClick={() => job("POST", `/api/workloads/${w.id}/drift`, { op: "restore" })}>Restore saved state</Btn>
      </> : null}
    </>}>
      <p style={{ marginTop: 0, color: "var(--ink-2)" }}>Someone changed this workload directly in Proxmox. Saved state is on the left, what is actually configured is on the right.</p>
      <table><thead><tr><th>Setting</th><th>Saved here</th><th>On the device</th></tr></thead><tbody>
        {dr.length ? dr.map((d: Any, i: number) => (
          <tr key={i}><td>{d.setting}</td><td className="mono">{d.saved}</td><td className="mono" style={{ color: "var(--warn)" }}>{d.actual}</td></tr>
        )) : <tr><td colSpan={3}>No differences right now.</td></tr>}
      </tbody></table>
      {fw ? <div className="banner" style={{ marginTop: 14 }}><span>△</span><div><b>Firewall rules differ</b>Adopting also accepts whatever ports are now open.</div></div> : null}
    </Frame>, true,
  );
}

/* ============================ smaller workload dialogs ============================ */
export function resize(id: Any) {
  const w = wl(id); if (!w) return;
  const n = nodeOf(w);
  simpleForm(`Change resources · ${w.name}`, { cores: w.cores, mem: w.mem, disk: w.dsize }, () => <>
    <div className="grid g3"><F.Num k="cores" label="vCPU cores" hint={n.cores ? `device has ${n.cores}` : ""} /><F.Num k="mem" label="Memory (MB)" /><F.Num k="disk" label="Disk (GB)" /></div>
    <div className="banner acc"><span>◈</span><div><b>Disk can grow but not shrink</b>{w.type === "vm"
      ? "CPU and memory changes on a running VM apply after a reboot (unless hotplug is on). The filesystem is grown inside the guest when SSH works."
      : "An LXC applies CPU and memory changes live."}</div></div>
  </>, "Apply", (d) => job("POST", `/api/workloads/${w.id}/resize`, { cores: +d.cores, mem: +d.mem, disk: +d.disk }));
}
export function snapshot(id: Any) {
  const w = wl(id); if (!w) return;
  simpleForm(`Snapshot · ${w.name}`, { name: "ec" + new Date().toISOString().slice(0, 16).replace(/[-:T]/g, ""), desc: "" }, () => <>
    <F.Text k="name" label="Name" hint="Letters, numbers, - and _. Starts with a letter." /><F.Text k="desc" label="Note" />
    <p className="hint">{w.type === "vm" ? "Disk-only snapshot (no RAM state)." : "Container snapshot."} On plain directory storage with raw disks Proxmox cannot snapshot — use a backup instead.</p>
  </>, "Take snapshot", (d) => job("POST", `/api/workloads/${w.id}/snapshot`, { op: "create", name: d.name, desc: d.desc }));
}
export function editTags(id: Any) {
  const w = wl(id); if (!w) return;
  simpleForm(`Owner and tags · ${w.name}`, { owner: w.owner === "—" ? "" : w.owner, tags: (w.tags || []).join(", ") }, () => <>
    <F.Text k="owner" label="Owner or team" /><F.Text k="tags" label="Tags" hint="Comma separated. Written to Proxmox as guest tags." />
  </>, "Save", (d) => act("PUT", `/api/workloads/${w.id}`, { owner: d.owner, tags: d.tags }, "Saved").then(() => Modal.close()));
}
export function tunnel(id: Any) {
  const w = wl(id); if (!w) return;
  const p = w.public || {};
  const ports = uniq<string>(DB.containers.filter((c: Any) => c.workload === w.id).flatMap((c: Any) => [...String(c.ports).matchAll(/(\d+)→/g)].map((m) => m[1])));
  const pOpts: Opt[] = (ports.length ? ports : ["80"]).concat(p.port && !ports.includes(String(p.port)) ? [String(p.port)] : []);
  simpleForm(`Public access · ${w.name}`, { en: p.enabled !== false, port: pick(pOpts, p.port || ports[0] || 80) }, () => <>
    <p style={{ marginTop: 0, color: "var(--ink-2)" }}>Tailscale Funnel gives this machine a public HTTPS URL on your tailnet domain with an automatic certificate. Your tailnet policy must allow Funnel for this node.</p>
    <F.Check k="en" title="Publish" />
    <F.Sel k="port" label="Local port to publish" opts={pOpts} hint="Ports come from the containers running here." />
    {!DB.settings.connectivity?.has_tailscale_authkey && !(w.software || []).includes("tailscale")
      ? <div className="banner"><span>△</span><div><b>No Tailscale auth key</b>Set one in Settings → Connectivity so the machine can join your tailnet.</div></div> : null}
  </>, "Apply", (d) => job("POST", `/api/workloads/${w.id}/tunnel`, { enabled: !!d.en, port: +d.port }));
}

/* ============================ restore / replace ============================ */
export async function restore(id: Any, volid?: string) {
  const w = wl(id); if (!w) return;
  let pts: Any[] = [];
  try { pts = await api("GET", `/api/workloads/${id}/backups`); } catch (e: Any) { toast("Failed", e.message, "err"); return; }
  if (!pts.length) { toast("No backups found", `Back up ${w.name} first`, "warn"); return; }
  const ptOpts: Opt[] = pts.map((p: Any, i: number) => [i, `${when(p.when)} — ${gb(p.size)} on ${p.storage}${p.node !== w.node ? " (" + (node(p.node) || {}).name + ")" : ""}`] as [Any, ReactNode]);
  const whereOpts: Opt[] = [["keep", "Same device, keep the current one as a copy (renamed -old)"], ["replace", "Same device, replacing the current workload"], ["other", "A different device"]];
  const dstOpts: Opt[] = DB.nodes.filter((n: Any) => n.id !== w.node).map((n: Any) => [n.id, n.name] as [Any, ReactNode]);
  const doRestore = (d: Any) => {
    const p = pts[+d.point], where = d.where;
    return job("POST", `/api/workloads/${id}/restore`, { volid: p.volid, src_node: p.node, where: where === "other" ? "other" : "same",
      dst_node: where === "other" ? +d.dst : null, keep: where !== "replace", start: !!d.start });
  };
  simpleForm(`Restore ${w.name}`,
    { point: pick(ptOpts, Math.max(0, pts.findIndex((p: Any) => p.volid === volid))), where: "keep", dst: pick(dstOpts), start: false },
    (f) => <>
      <F.Sel k="point" label="Restore point" opts={ptOpts} />
      <F.Sel k="where" label="Restore to" opts={whereOpts} />
      <div id="rdst" style={{ display: f.d.where === "other" ? "block" : "none" }}><F.Sel k="dst" label="Device" opts={dstOpts} /></div>
      <F.Check k="start" title="Start it after the restore" />
      <div className="banner"><span>△</span><div><b>Replacing overwrites the disks</b>“Replacing the current workload” stops it and writes the backup over it.</div></div>
    </>, "Start restore", doRestore);
}
export function replaceDevice() {
  if (DB.nodes.length < 2) { toast("Claim the replacement first", "Enroll and claim the spare device, then come back here", "warn"); return; }
  const off = DB.nodes.find((n: Any) => n.status === "offline") || DB.nodes[0];
  const oldOpts: Opt[] = DB.nodes.map((n: Any) => [n.id, `${n.name} — ${site(n.site).name} (${n.status})`] as [Any, ReactNode]);
  const newOpts: Opt[] = DB.nodes.map((n: Any) => [n.id, `${n.name} — ${site(n.site).name}`] as [Any, ReactNode]);
  simpleForm("Replace a failed device",
    { old: pick(oldOpts, off.id), new: pick(newOpts, (DB.nodes.find((n: Any) => n.id !== off.id) || {}).id), restore: true, keepip: true }, () => <>
      <p style={{ marginTop: 0, color: "var(--ink-2)" }}>Enroll and claim the spare first, then pick it here. Every guest of the old device is restored on the new one from its latest backup.</p>
      <F.Sel k="old" label="Device being replaced" opts={oldOpts} />
      <F.Sel k="new" label="Replacement device" opts={newOpts} />
      <F.Check k="restore" title="Restore all guests from the latest backup" sub="Backups must be on shared storage (NFS/PBS) if the old device is dead; if it still answers they are copied across." />
      <F.Check k="keepip" title="Keep the same MAC addresses (and so DHCP leases), IPs and hostnames" />
    </>, "Start replacement", (d) => job("POST", "/api/backups/replace-device", { old: +d.old, new: +d.new, restore: !!d.restore, keepip: !!d.keepip }));
}

/* ============================ rollouts ============================ */
async function rolloutLoad(f: Form, setVer: (v: string) => void) {
  try {
    const r = await api("GET", "/api/rollouts/stack/" + encodeURIComponent(f.d.stack));
    f.set("content", r.content); setVer(`current v${r.version} → v${r.version + 1}`);
  } catch { /* ignore */ }
}
function RolloutStack({ f, stacks }: { f: Form; stacks: string[] }) {
  const [ver, setVer] = useState("");
  useEffect(() => { fire(rolloutLoad(f, setVer)); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  return <>
    <F.Sel k="stack" label="Stack (every machine running a stack with this name)" opts={stacks} onChange={() => fire(rolloutLoad(f, setVer))} />
    <div className="editor"><div className="eh"><b>New compose file</b><div className="spacer" /><span className="muted" id="ro-ver">{ver}</span></div>
      <textarea spellCheck={false} value={f.d.content ?? ""} onChange={(e) => f.set("content", e.target.value)} /></div>
  </>;
}
export function rollout(kind?: string) {
  const k0 = kind || "stack";
  const stacks = uniq<string>(DB.stacks.filter((s: Any) => s.kind === "compose").map((s: Any) => s.name));
  const T = (k: string, l: string) => <button className={k0 === k ? "on" : ""} onClick={() => rollout(k)}>{l}</button>;
  const pbOpts: Opt[] = DB.playbooks.map((p: Any) => p.name);
  const whereOpts: Opt[] = ["All sites", ...DB.sites.map((s: Any) => "Site: " + s.name), ...allTags().map((t) => "Tag: " + t)];
  const waveOpts: Opt[] = ["Canary → 25% → 50% → 100%", "Canary → 100%", "All at once"];
  const hasStack = k0 === "stack" && stacks.length > 0;
  const init: Any = { where: pick(whereOpts), wave: pick(waveOpts), gate: true, approval: true, auto: true, window: false };
  if (hasStack) { init.stack = stacks[0]; init.content = ""; }
  if (k0 === "playbook") init.playbook = pick(pbOpts, "patch_os.yml");
  const startRollout = (d: Any) =>
    job("POST", "/api/rollouts", { kind: k0, stack: d.stack, content: d.content, playbook: d.playbook, where: d.where,
      wave: d.wave, gate: !!d.gate, auto: !!d.auto, window: !!d.window, approval: !!d.approval }, { go: false }).then(() => go("rollouts"));
  simpleForm("Start a rollout", init, (f) => <>
    <div className="pill-toggle" style={{ marginBottom: 14 }}>{T("stack", "Compose stack update")}{T("playbook", "Playbook")}{T("agent", "Edge agent upgrade")}</div>
    {k0 === "stack" ? (stacks.length ? <RolloutStack f={f} stacks={stacks} />
      : <div className="banner"><span>△</span><div><b>No compose stacks yet</b>Deploy a stack first; rollouts update every machine running it.</div></div>) : null}
    {k0 === "playbook" ? <F.Sel k="playbook" label="Playbook" opts={pbOpts} /> : null}
    {k0 === "agent" ? <p>Pushes agent {DB.meta.agent_version} to every device in scope over SSH.</p> : null}
    <F.Sel k="where" label="Where" opts={whereOpts} />
    <F.Sel k="wave" label="Wave plan" opts={waveOpts} />
    <F.Check k="gate" title="Wait for health checks between waves" sub="All containers healthy for 60 seconds before the next wave starts." />
    <F.Check k="approval" title="Wait for my approval between waves" />
    <F.Check k="auto" title="Roll back automatically on failure" />
    <F.Check k="window" title="Only inside the maintenance window" sub={DB.settings.window?.value || ""} />
  </>, "Start rollout", startRollout, true);
}

/* ============================ alert rules ============================ */
export function alertRule() {
  const metricOpts = ["Memory usage", "CPU usage", "Disk usage", "Container restarts", "Agent check-in", "Backup result", "Certificate expiry"];
  const opOpts = ["is above", "is below", "changes to"];
  const forOpts = ["1 minute", "5 minutes", "10 minutes", "30 minutes"];
  const scopeOpts = ["All sites", "All nodes", "All containers", ...DB.sites.map((s: Any) => "Site: " + s.name), ...allTags().map((t) => "Tag: " + t),
    ...DB.workloads.map((w: Any) => "Workload: " + w.name)];
  const sevOpts = ["critical", "warning", "info"];
  const chanOpts = ["Email", "Email, Slack", "Slack", "PagerDuty", "Email, PagerDuty", "Webhook"];
  simpleForm("New alert rule", {
    name: "Memory above 90% for 10 minutes", metric: metricOpts[0], op: opOpts[0], val: 90, for: "10 minutes", scope: pick(scopeOpts), sev: "warning", chan: chanOpts[0],
  }, () => <>
    <F.Text k="name" label="Rule name" />
    <F.Sel k="metric" label="Watch" opts={metricOpts} />
    <div className="grid g3"><F.Sel k="op" label="Condition" opts={opOpts} /><F.Num k="val" label="Value (%, count, minutes or days)" /><F.Sel k="for" label="For" opts={forOpts} /></div>
    <F.Sel k="scope" label="Applies to" opts={scopeOpts} />
    <F.Sel k="sev" label="Severity" opts={sevOpts} />
    <F.Sel k="chan" label="Notify" opts={chanOpts} />
  </>, "Create rule", (d) => act("POST", "/api/rules", { name: d.name, metric: d.metric, op: d.op, val: +d.val, for: d.for, scope: d.scope, sev: d.sev, chan: d.chan },
    "Alert rule created", d.name).then(() => { Modal.close(); go("alerts"); }));
}

/* ============================ sites ============================ */
const WINDOWS = ["Sunday 02:00 – 04:00", "Saturday 02:00 – 04:00", "Daily 01:00 – 03:00", "No restriction"];
export function addSite() {
  simpleForm("Add a site", { name: "", city: "", window: WINDOWS[0] }, () => <>
    <F.Text k="name" label="Site name" /><F.Text k="city" label="City" /><F.Sel k="window" label="Maintenance window" opts={WINDOWS} />
  </>, "Create site", (d) => act("POST", "/api/sites", { name: d.name, city: d.city, window: d.window }, "Site created", "Now claim a device into it")
    .then(() => { Modal.close(); go("sites"); }));
}
export function editSite(id: Any) {
  const s = site(id);
  Modal.open(
    <FormModal title="Edit site" init={{ name: s.name ?? "", city: s.city ?? "", window: pick(WINDOWS, s.window) }}
      body={() => <>
        <F.Text k="name" label="Site name" /><F.Text k="city" label="City" /><F.Sel k="window" label="Maintenance window" opts={WINDOWS} />
      </>}
      footer={(f) => <>
        <Btn danger onClick={() => act("DELETE", `/api/sites/${s.id}`, undefined, "Site deleted").then(() => Modal.close())}>Delete site</Btn>
        <div className="spacer" />
        <button className="btn" onClick={Modal.close}>Cancel</button>
        <Btn primary onClick={() => act("PUT", `/api/sites/${s.id}`, { name: f.d.name, city: f.d.city, window: f.d.window }, "Site saved").then(() => Modal.close())}>Save</Btn>
      </>} />,
  );
}

/* ============================ images / private apps ============================ */
export function addImage() {
  const kinds = ["Cloud image", "LXC template", "ISO"];
  simpleForm("Add an image", { kind: kinds[0], url: "", name: "", cache: true, approve: false }, () => <>
    <F.Sel k="kind" label="Kind" opts={kinds} />
    <F.Text k="url" label="Direct download URL" hint="Cloud images: .img or .qcow2 (Ubuntu, Debian, …). LXC: a .tar.xz/.tar.zst template. Pick the arch that matches your devices."
      ph="https://cloud-images.ubuntu.com/noble/current/noble-server-cloudimg-arm64.img" />
    <F.Text k="name" label="Display name (optional)" />
    <F.Check k="cache" title="Download to every device now" sub="Each device downloads it once so deployments do not wait on the uplink." />
    <F.Check k="approve" title="Mark as approved for production" />
  </>, "Add image", (d) => job("POST", "/api/images", { kind: d.kind, url: d.url, name: d.name, cache: !!d.cache, approve: !!d.approve }, { go: !!d.cache }));
}
export function publishApp() {
  simpleForm("Publish a private app", { icon: "📦", ram: "512 MB", port: 0, name: "", desc: "", compose: SAMPLE_COMPOSE }, (f) => <>
    <p style={{ marginTop: 0, color: "var(--ink-2)" }}>Private apps appear in the catalog for your organization only. The compose file is validated against your policy.</p>
    <div className="grid g3"><F.Text k="icon" label="Icon (emoji)" /><F.Text k="ram" label="Memory" /><F.Num k="port" label="Web port (for public URL)" /></div>
    <F.Text k="name" label="App name" /><F.Text k="desc" label="One-line description" />
    <div className="editor" style={{ minHeight: 160 }}><div className="eh"><b>compose template</b></div>
      <textarea spellCheck={false} style={{ minHeight: 200 }} value={f.d.compose ?? ""} onChange={(e) => f.set("compose", e.target.value)} /></div>
  </>, "Publish", (d) => act("POST", "/api/catalog", { name: d.name, desc: d.desc, icon: d.icon, ram: d.ram, port: +d.port, compose: d.compose },
    "Published to your catalog", "Visible to everyone in the organization").then(() => Modal.close()), true);
}
